import { ENTITLEMENT_STATUSES, type ConsoleModule, type EntitlementStatus } from '@sentinel/shared';
import { Panel, SelectField, StateBadge, Table, type Column } from '@sentinel/ui';
import { QueryState } from '@sentinel/web-core';
import { useTranslation } from 'react-i18next';
import { useConsoleCan } from '@/auth/use-console-can';
import type { useOrganization } from './use-organization';

const K = 'console.organization.modules';
/** A trial lasts a month unless the platform sets another end. */
const TRIAL_DAYS = 30;

type Props = Pick<ReturnType<typeof useOrganization>, 'modules' | 'setModule'>;

/** The module switch (T-04): the platform makes a module available (included or on trial) or
 *  takes it away; the organization then switches it on or off by itself. */
export function ModulesPanel({ modules, setModule }: Props) {
  const { t } = useTranslation();
  const canManage = useConsoleCan('console.entitlements:manage');
  const change = (module: ConsoleModule, entitlement: EntitlementStatus) => {
    const trialEndsAt =
      entitlement === 'trial' ? new Date(Date.now() + TRIAL_DAYS * 86_400_000).toISOString() : null;
    setModule.mutate({ key: module.key, availability: { entitlement, trialEndsAt } });
  };
  const columns: Column<ConsoleModule>[] = [
    { key: 'name', header: t(`${K}.name`), render: (m) => t(`modules.names.${m.key}`) },
    {
      key: 'availability',
      header: t(`${K}.availability`),
      render: (m) =>
        m.isCore ? (
          t(`${K}.core`)
        ) : (
          <SelectField
            id={`module-${m.key}`}
            label={t(`${K}.availabilityOf`, { name: t(`modules.names.${m.key}`) })}
            labelHidden
            value={m.entitlement}
            disabled={!canManage || setModule.isPending}
            onChange={(e) => change(m, e.target.value as EntitlementStatus)}
            options={ENTITLEMENT_STATUSES.map((s) => ({
              value: s,
              label: t(`${K}.entitlements.${s}`),
            }))}
          />
        ),
    },
    {
      key: 'enabled',
      header: t(`${K}.inUse`),
      render: (m) => (
        <StateBadge tone={m.enabled ? 'ok' : 'off'} label={t(`${K}.${m.enabled ? 'on' : 'off'}`)} />
      ),
    },
  ];
  return (
    <Panel flush title={t(`${K}.title`)}>
      <QueryState query={modules}>
        {(data) => (
          <Table
            caption={t(`${K}.title`)}
            columns={columns}
            rows={data.items}
            rowKey={(m) => m.key}
          />
        )}
      </QueryState>
    </Panel>
  );
}
